/**
 * Collection Navigation Memory System
 * Stores the last visited collection URL and the exact product position
 * to improve "Continue Shopping" experience
 */

class CollectionNavigation {
  constructor() {
    this.COLLECTION_KEY = 'mcgills_last_collection';
    this.PRODUCT_KEY = 'mcgills_clicked_product';
    this.init();
  }

  init() {
    // Track collection pages
    if (this.isCollectionPage()) {
      this.saveCurrentCollection();
      this.trackProductClicks();
      this.restoreProductPosition();
    }

    // Setup Continue Shopping buttons
    this.setupContinueShoppingButtons();
  }

  isCollectionPage() {
    return document.body.classList.contains('template-collection');
  }

  isProductPage() {
    return document.body.classList.contains('template-product');
  }

  saveCurrentCollection() {
    try {
      const collectionData = {
        url: window.location.href,
        pathname: window.location.pathname,
        timestamp: Date.now()
      };
      sessionStorage.setItem(this.COLLECTION_KEY, JSON.stringify(collectionData));
    } catch (e) {
      // Silent fail
    }
  }

  trackProductClicks() {
    // Track clicks on product links
    document.addEventListener('click', (e) => {
      const productLink = e.target.closest('a[href*="/products/"]');
      if (productLink && productLink.href) {
        try {
          // Extract product handle from URL
          const url = new URL(productLink.href);
          const pathParts = url.pathname.split('/').filter(p => p);
          const productIndex = pathParts.indexOf('products');
          const handle = productIndex >= 0 && pathParts[productIndex + 1] 
            ? pathParts[productIndex + 1] 
            : null;

          if (handle) {
            const productData = {
              handle: handle,
              timestamp: Date.now()
            };
            sessionStorage.setItem(this.PRODUCT_KEY, JSON.stringify(productData));
          }
        } catch (e) {
          // Silent fail
        }
      }
    });
  }

  restoreProductPosition() {
    try {
      const productData = sessionStorage.getItem(this.PRODUCT_KEY);
      if (!productData) {
        return;
      }

      const data = JSON.parse(productData);
      
      // Check if data is less than 5 minutes old
      if (Date.now() - data.timestamp > 300000) {
        sessionStorage.removeItem(this.PRODUCT_KEY);
        return;
      }

      // Wait for images to load before scrolling
      const scrollToProduct = () => {
        // Find all grid items (product containers)
        const gridItems = document.querySelectorAll('.grid__item');
        let targetProduct = null;

        for (const item of gridItems) {
          // Look for product link inside this grid item
          const productLink = item.querySelector('a[href*="/products/"]');
          if (productLink) {
            try {
              // Extract handle from the link
              const url = new URL(productLink.href);
              const pathParts = url.pathname.split('/').filter(p => p);
              const productIndex = pathParts.indexOf('products');
              const handle = productIndex >= 0 && pathParts[productIndex + 1] 
                ? pathParts[productIndex + 1] 
                : null;

              if (handle === data.handle) {
                targetProduct = item;
                break;
              }
            } catch (e) {
              // Silent fail
            }
          }
        }

        if (targetProduct) {
          // Scroll to the product with offset for header
          const headerOffset = 120;
          const elementPosition = targetProduct.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });

          // Add a subtle highlight effect
          const cardWrapper = targetProduct.querySelector('.card-wrapper');
          if (cardWrapper) {
            cardWrapper.style.transition = 'box-shadow 0.5s ease, transform 0.3s ease';
            cardWrapper.style.boxShadow = '0 0 0 3px rgba(var(--color-button), 0.3)';
            cardWrapper.style.transform = 'scale(1.02)';
            setTimeout(() => {
              cardWrapper.style.boxShadow = 'none';
              cardWrapper.style.transform = 'scale(1)';
            }, 800);
          }

          // Clear the saved product after successful scroll
          sessionStorage.removeItem(this.PRODUCT_KEY);
        } else {
          sessionStorage.removeItem(this.PRODUCT_KEY);
        }
      };

      // Wait for DOM and images to load
      if (document.readyState === 'complete') {
        setTimeout(scrollToProduct, 200);
      } else {
        window.addEventListener('load', () => {
          setTimeout(scrollToProduct, 200);
        });
      }
    } catch (e) {
      // Silent fail
    }
  }

  getLastCollection() {
    try {
      const data = sessionStorage.getItem(this.COLLECTION_KEY);
      if (data) {
        const collectionData = JSON.parse(data);
        // Check if data is less than 1 hour old
        if (Date.now() - collectionData.timestamp < 3600000) {
          return collectionData.url;
        }
      }
    } catch (e) {
      // Silent fail
    }
    return null;
  }

  setupContinueShoppingButtons() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('[data-continue-shopping]');
      if (link) {
        e.preventDefault();
        this.handleContinueShopping();
      }
    });
  }

  handleContinueShopping() {
    const lastCollection = this.getLastCollection();
    
    if (lastCollection) {
      window.location.href = lastCollection;
    } else if (document.referrer && document.referrer.includes(window.location.hostname)) {
      window.location.href = document.referrer;
    } else {
      // Fallback to collections page or home
      window.location.href = '/';
    }
  }

  // Public method for manual trigger
  static continueShopping() {
    const nav = new CollectionNavigation();
    nav.handleContinueShopping();
  }
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CollectionNavigation();
  });
} else {
  new CollectionNavigation();
}

// Expose for manual use
window.CollectionNavigation = CollectionNavigation;
